import React, { useState, useEffect, useCallback } from 'react'
import { connect } from 'react-redux'
import { useHistory } from 'react-router-dom'
import axios from 'axios'
import {
  Typography,
  Chip,
  Button,
  CircularProgress,
  Snackbar
} from '@material-ui/core'
import { OpenInNew as OpenInNewIcon, Send as SendIcon } from '@material-ui/icons'
import GroupsTab from './GroupsTab'
import ItemsTab from './ItemsTab'
import EstimateTab from './EstimateTab'
import ExportPreviewTab from './ExportPreviewTab'
import BidEstimationHeader from '../shared/BidEstimationHeader'

const STATUS_CHIP_PROPS = {
  parsing: { color: 'default', label: 'Parsing' },
  parsed: { color: 'primary', label: 'Parsed' },
  review_needed: { color: 'secondary', label: 'Review Needed', style: { backgroundColor: '#f57c00', color: '#fff' } },
  parse_failed: { color: 'secondary', label: 'Parse Failed' },
  mapped: { color: 'primary', label: 'Mapped' }
}

const TAB_LABELS = ['Groups', 'Items', 'Estimate', 'Export Preview']

const BidResponseWorkspace = ({ projectId, packageId, url }) => {
  const history = useHistory()
  const [pkg, setPkg] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [tab, setTab] = useState(1)
  const [itemsGroupFilter, setItemsGroupFilter] = useState('all')
  const [sendResult, setSendResult] = useState(null)
  const [generatingAssembly, setGeneratingAssembly] = useState(false)

  // Jump from the Groups tab to the Items tab pre-filtered by a specific group.
  const handleGroupClick = (groupId) => {
    setItemsGroupFilter(groupId || 'all')
    setTab(1)
  }

  const fetchPackage = useCallback(() => {
    setLoading(true)
    setError('')
    axios.get(`${url}/api/bid-estimation/bid-packages/${packageId}`)
      .then(res => {
        setPkg(res.data)
        setLoading(false)
      })
      .catch(err => {
        setError(err.response?.data?.message || 'Failed to load bid package.')
        setLoading(false)
      })
  }, [packageId, url])

  useEffect(() => {
    if (!packageId) return
    fetchPackage()
  }, [packageId, fetchPackage])

  const handleExport = () => {
    window.location = url + '/api/bid-estimation/bid-packages/' + packageId + '/export'
  }

  const handleOpenAssemblyWorkbench = () => {
    if (!projectId) {
      setSendResult({ success: false, message: 'No project ID — cannot navigate to scope' })
      return
    }
    setSendResult({ success: true, message: 'Opening Assembly Workbench' })
    setTimeout(() => history.push(`/bid-estimation/${projectId}/scope`), 600)
  }

  const handleGenerateAssembly = async () => {
    if (!projectId) {
      setSendResult({ success: false, message: 'No project ID — cannot generate assembly items' })
      return
    }
    try {
      setGeneratingAssembly(true)
      const res = await axios.post(`${url}/api/bid-estimation/projects/${projectId}/scope/generate`)
      const generated = res.data?.generated?.total || 0
      setSendResult({ success: true, message: `Created/updated ${generated} Assembly Items` })
      setTimeout(() => history.push(`/bid-estimation/${projectId}/scope`), 700)
    } catch (err) {
      setSendResult({ success: false, message: err.response?.data?.error || 'Failed to generate Assembly Items' })
    } finally {
      setGeneratingAssembly(false)
    }
  }

  const getStatusChipProps = (status) => {
    return STATUS_CHIP_PROPS[status] || { color: 'default', label: status || 'Unknown' }
  }

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
        <CircularProgress />
      </div>
    )
  }

  if (error) {
    return (
      <div style={{ padding: 24 }}>
        <Typography color="error">{error}</Typography>
      </div>
    )
  }

  const statusProps = getStatusChipProps(pkg && pkg.status)

  return (
    <div className="hmi-page" style={{ position: 'fixed', inset: 0, zIndex: 1200, overflow: 'auto' }}>
      <BidEstimationHeader
        breadcrumbs={[
          { label: 'Bid Estimation', to: '/bid-estimation' },
          ...(projectId ? [{ label: 'Project', to: `/bid-estimation/${projectId}` }] : []),
          { label: 'Bid Response' }
        ]}
        title={pkg ? pkg.name : `Bid Response ${packageId}`}
        statusChip={{
          label: statusProps.label,
          style: statusProps.style || { backgroundColor: 'var(--df-bg-2, #ece5d3)', color: 'var(--df-ink-2, #3d463a)' }
        }}
        actions={(
          <>
            {pkg && pkg.sourceFile && pkg.sourceFile.s3Url && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<OpenInNewIcon />}
                href={pkg.sourceFile.s3Url}
                target="_blank"
                rel="noopener noreferrer"
                title={pkg.sourceFile.originalFilename || 'Original file'}
                style={{ color: '#fff', borderColor: 'rgba(255,255,255,0.5)' }}
              >
                View Original
              </Button>
            )}
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={handleExport}
            >
              Export
            </Button>
            <Button
              variant="contained"
              size="small"
              startIcon={<SendIcon />}
              onClick={handleGenerateAssembly}
              disabled={generatingAssembly}
              style={{ backgroundColor: '#2e7d32', color: '#fff', marginLeft: 8 }}
            >
              {generatingAssembly ? 'Creating Assembly Items...' : 'Create Assembly Items'}
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={handleOpenAssemblyWorkbench}
              style={{ marginLeft: 8 }}
            >
              Open Workbench
            </Button>
          </>
        )}
      />

      {/* Send result feedback */}
      <Snackbar
        open={!!sendResult}
        autoHideDuration={4000}
        onClose={() => setSendResult(null)}
        message={sendResult?.message || ''}
      />

      {/* Tabs */}
      <div className="hmi-tab-bar">
        {TAB_LABELS.map((label, i) => (
          <button key={i} className={`hmi-tab${tab === i ? ' active' : ''}`} onClick={() => setTab(i)}>
            {label}
          </button>
        ))}
      </div>

      <div className="hmi-body">
          {tab === 0 && (
            <GroupsTab
              groups={pkg ? pkg.groups : []}
              onGroupUpdate={fetchPackage}
              onGroupClick={handleGroupClick}
              url={url}
            />
          )}
          {tab === 1 && (
            <ItemsTab
              packageId={packageId}
              projectId={projectId}
              url={url}
              onItemUpdate={fetchPackage}
              initialGroupFilter={itemsGroupFilter}
              onGroupFilterChange={setItemsGroupFilter}
              sourceFile={pkg ? pkg.sourceFile : null}
            />
          )}
          {tab === 2 && <EstimateTab packageId={packageId} url={url} onUpdate={fetchPackage} />}
          {tab === 3 && <ExportPreviewTab packageId={packageId} url={url} />}
      </div>
    </div>
  )
}

const mapStateToProps = (state) => ({
  url: state.url
})

export default connect(mapStateToProps)(BidResponseWorkspace)
